import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Grid,
    TextField,
    Typography,
    Button,
    Divider,
    IconButton,
} from "@mui/material";
import authService from "../Auth/AuthService";
import { Edit, UploadFile } from "@mui/icons-material";
import ModalView from "../model/ModelView";
import SelfieCamera from "../emp-components/emp-card/SelfieCamera";

export default function EmployeeProfile({ employeeData, onUpdate }) {
    const [editMode, setEditMode] = useState(false);
    const [form, setForm] = useState(null);
    const hasFetched = useRef(false);
    const [openCamera, setOpenCamera] = useState(false);

    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;
        loadEmployees();
    }, []);

    const loadEmployees = async () => {
        const url = authService.pathUrl("GET", "/employees/profile");
        const data = await authService.getAuth(url);
        setForm(data || {});
    };

    if (!form) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleAddressChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({
            ...prev,
            addressDTO: {
                ...prev.addressDTO,
                [name]: value,
            },
        }));
    };

    const handleSave = () => {
        onUpdate?.(form);
        setEditMode(false);
    };

    const handleCancel = () => {
        // setForm(employeeData); // reset changes
        setEditMode(false);
        setOpenCamera(false);
    };
    const handleUpdate = async (updatedForm) => {
        console.log('employee update Data:', updatedForm);
        try {
            const url = authService.pathUrl("PUT", "/employees/" + form.id)
            await authService.putAuth(url, updatedForm);
        } catch (err) {
            console.error('Error:', err);
        }
    };

    return (
        <Box display="flex" justifyContent="center" mt={4}>
            <Card sx={{
                width: "100%", //maxWidth: 900,
                borderRadius: 3
            }}>
                <CardContent>
                    {/* Header */}
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h5" fontWeight="bold">
                            Employee Profile
                        </Typography>

                        {!editMode && !form?.isVerify ? (<Box display="flex" gap={1}>
                            <IconButton color="warning" title="Take Selfie" onClick={() => setOpenCamera(true)}>
                                <UploadFile />
                            </IconButton>
                            <IconButton color="warning" onClick={() => setEditMode(true)}>
                                <Edit />
                            </IconButton>
                        </Box>
                        ) : (
                            <Box display="flex" gap={1}>
                                <Button color="error" onClick={handleCancel}>
                                    Cancel
                                </Button>
                                <Button variant="contained" onClick={handleSave}>
                                    Save
                                </Button>
                            </Box>
                        )}
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* BASIC INFO */}
                    <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                        Basic Information
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="First Name"
                                name="firstName"
                                value={form.firstName || ""}
                                onChange={handleChange}
                                fullWidth
                                disabled={!editMode}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Last Name"
                                name="lastName"
                                value={form.lastName || ""}
                                onChange={handleChange}
                                fullWidth
                                disabled={!editMode}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Email"
                                value={form.email || ""}
                                fullWidth
                                disabled
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Mobile"
                                value={form.mobile || ""}
                                fullWidth
                                disabled
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Gender"
                                name="gender"
                                value={form.gender || ""}
                                onChange={handleChange}
                                fullWidth
                                disabled={!editMode}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Date of Birth"
                                name="dateOfBirth"
                                type="date"
                                value={form.dateOfBirth || ""}
                                onChange={handleChange}
                                fullWidth
                                disabled={!editMode}
                                slotProps={{ inputLabel: { shrink: true } }}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    {/* ADDRESS */}
                    <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                        Address
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                label="Address Line 1"
                                name="addressLine1"
                                value={form.addressDTO?.addressLine1 || ""}
                                onChange={handleAddressChange}
                                fullWidth
                                disabled={!editMode}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                label="Address Line 2"
                                name="addressLine2"
                                value={form.addressDTO?.addressLine2 || ""}
                                onChange={handleAddressChange}
                                fullWidth
                                disabled={!editMode}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="City"
                                name="city"
                                value={form.addressDTO?.city || ""}
                                onChange={handleAddressChange}
                                fullWidth
                                disabled={!editMode}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="State"
                                name="state"
                                value={form.addressDTO?.state || ""}
                                onChange={handleAddressChange}
                                fullWidth
                                disabled={!editMode}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Pincode"
                                name="pincode"
                                value={form.addressDTO?.pincode || ""}
                                onChange={handleAddressChange}
                                fullWidth
                                disabled={!editMode}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    {/* EMPLOYMENT */}
                    <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                        Employment Details
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Qualification"
                                name="qualification"
                                value={form.qualification || ""}
                                onChange={handleChange}
                                fullWidth
                                disabled={!editMode}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Specialization"
                                name="specialization"
                                value={form.specialization || ""}
                                onChange={handleChange}
                                fullWidth
                                disabled={!editMode}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Joining Date"
                                name="joiningDate"
                                type="date"
                                value={form.joiningDate || ""}
                                onChange={handleChange}
                                fullWidth
                                disabled={!editMode}
                                slotProps={{ inputLabel: { shrink: true } }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Experience"
                                name="experience"
                                value={form.experience || ""}
                                onChange={handleChange}
                                fullWidth
                                disabled={!editMode}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            <ModalView
                size="sm"
                open={openCamera}
                handleClose={handleCancel}
                title="Selfie"
                content={
                    <SelfieCamera
                        emp={form}
                        onSuccess={(fileData) => {
                            setOpenCamera(false);
                            const updatedForm = {
                                ...form,
                                photo: fileData.path
                            };
                            setForm(updatedForm);
                            handleUpdate(updatedForm);  // 👈 pass fresh data
                        }}
                    />
                }
            />
        </Box>
    );
}
